import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAudit = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = actionFilter ? `?action=${actionFilter}` : '';
      const res = await fetch(`${API_URL}/api/platform/audit${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setTotal(data.total);
      }
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const actionColor = (action) => {
    if (action.includes('activate')) return action.includes('deactivate') ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400';
    if (action.includes('assign')) return 'bg-blue-500/10 text-blue-400';
    if (action.includes('discount')) return 'bg-violet-500/10 text-violet-400';
    return 'bg-slate-700/30 text-slate-400';
  };

  const actionTypes = [
    'activate_company', 'deactivate_company', 'assign_plan',
    'force_activate_subscription', 'force_deactivate_subscription',
    'create_discount', 'deactivate_discount'
  ];

  return (
    <div className="space-y-4" data-testid="platform-audit">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Audit Log</h1>
          <p className="text-xs text-slate-500">{total} platform actions logged</p>
        </div>
        <select
          className="h-8 px-2 text-xs bg-slate-900 border border-slate-800 rounded text-slate-300"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="platform-audit-filter"
        >
          <option value="">All Actions</option>
          {actionTypes.map(a => (
            <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mx-auto">
              <Shield className="w-5 h-5 text-slate-500" />
            </div>
            <p className="text-xs text-slate-500">No platform actions logged yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-[10px] text-slate-500 font-medium">Timestamp</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Admin</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Action</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Entity</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.log_id} className="border-slate-800/50 hover:bg-slate-800/30" data-testid={`audit-row-${log.log_id}`}>
                  <TableCell className="text-[10px] text-slate-500 whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <p className="text-xs text-slate-300">{log.admin_name}</p>
                    <p className="text-[10px] text-slate-500">{log.admin_role}</p>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${actionColor(log.action)}`}>
                      {log.action.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <p className="text-[10px] text-slate-400">{log.entity_type}</p>
                    <p className="text-[10px] text-slate-600 font-mono">{log.entity_id}</p>
                  </TableCell>
                  <TableCell className="max-w-[250px]">
                    <p className="text-[10px] text-slate-400 truncate" title={log.details}>
                      {log.details || '-'}
                    </p>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default PlatformAudit;
